import {
  AdapterMessage,
  AdapterReply,
  MinimalProviderConfig,
  ModelInfo,
  OnChunk,
  TokenUsage,
  createProviderApiError,
  normalizeEndpoint,
  recordLiveContextTokens,
  safeErrorDetail,
  wrapNetworkError,
} from "./types";
import { httpRequest, httpStream, HttpResponse, HttpStreamResponse } from "../http";

// Covers ChatGPT, DeepSeek, Kimi/Moonshot, and Qwen (DashScope compatible mode) — they all
// speak the same OpenAI-shaped request/response format.

// `signal` is the Stop button / typing-bubble click (chat.ts) — passed straight through to
// httpStream, the only way a request in flight ever ends early (see http.ts's own comment for
// why there's no timeout). `onChunk` (optional) fires with each incremental slice of reply text
// as it streams in — see adapters/types.ts's OnChunk for the exact contract.
//
// Streams via SSE (`stream: true`) — every provider this adapter serves (ChatGPT, DeepSeek,
// Kimi, Qwen) is OpenAI-shaped for the *non*-streaming response too, but streaming conformance
// for the third-party ones is genuinely less certain than for OpenAI itself; `stream_options`
// (usage-in-stream) in particular is a best-effort OpenAI extension, not something every
// OpenAI-compatible backend is guaranteed to honor — treated the same graceful-undefined way
// missing usage already was in the non-streaming version of this function.
export async function sendMessage(
  config: MinimalProviderConfig,
  messages: AdapterMessage[],
  signal?: AbortSignal,
  onChunk?: OnChunk
): Promise<AdapterReply> {
  const url = `${normalizeEndpoint(config.endpoint)}/chat/completions`;

  const outgoingMessages: (AdapterMessage | { role: "system"; content: string })[] =
    config.systemPrompt
      ? [{ role: "system", content: config.systemPrompt }, ...messages]
      : messages;

  let response: HttpStreamResponse;
  try {
    response = await httpStream(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${config.apiKey}`,
      },
      body: JSON.stringify({
        model: config.model,
        messages: outgoingMessages,
        stream: true,
        stream_options: { include_usage: true },
        ...(config.temperature !== undefined ? { temperature: config.temperature } : {}),
      }),
      signal,
    });
  } catch (err) {
    wrapNetworkError(config.name, err);
  }

  if (!response.ok) {
    throw createProviderApiError(
      `${config.name} returned an error (${response.status}): ${await safeErrorDetail(response)}`
    );
  }

  let text = "";
  let usage: TokenUsage | undefined;
  let finishReason: string | undefined;
  try {
    for await (const frame of response.events()) {
      // OpenAI's own end-of-stream sentinel — not a JSON payload, must be checked before parsing.
      if (frame.data === "[DONE]") break;
      let parsed: {
        choices?: { delta?: { content?: unknown }; finish_reason?: unknown }[];
        usage?: { prompt_tokens?: unknown; completion_tokens?: unknown };
      };
      try {
        parsed = JSON.parse(frame.data);
      } catch {
        // A malformed/unexpected frame shouldn't fail an otherwise-good reply — skip it rather
        // than aborting text already received.
        continue;
      }
      const delta = parsed.choices?.[0]?.delta?.content;
      if (typeof delta === "string" && delta) {
        text += delta;
        onChunk?.(delta);
      }
      // Present on the terminal chunk once generation stops — "length" means the output-token
      // cap was hit mid-reply, same class of event as Anthropic's "stop_reason": "max_tokens".
      if (typeof parsed.choices?.[0]?.finish_reason === "string") {
        finishReason = parsed.choices[0].finish_reason;
      }
      // Only present on the terminal usage-only chunk when stream_options.include_usage was
      // honored (see the function comment above) — every other chunk's `usage` is absent.
      if (
        typeof parsed.usage?.prompt_tokens === "number" &&
        typeof parsed.usage?.completion_tokens === "number"
      ) {
        usage = {
          inputTokens: parsed.usage.prompt_tokens,
          outputTokens: parsed.usage.completion_tokens,
        };
      }
    }
  } catch (err) {
    wrapNetworkError(config.name, err);
  }

  if (!text) {
    throw createProviderApiError(`${config.name} returned a response in an unexpected shape.`);
  }

  // Unlike a request that's too long (which errors up front), hitting the output-token ceiling
  // just silently stops generation mid-reply — "finish_reason": "length" is the only signal this
  // happened. Surfaced directly in the returned text, same convention anthropic.ts already uses
  // for its own equivalent case, so a reply cut off mid-scene reads as a hit limit here too
  // instead of silently ending with no explanation.
  const finalText =
    finishReason === "length"
      ? `${text}\n\n[Reply cut off — reached this model's output limit.]`
      : text;

  return { text: finalText, usage };
}

export async function listModels(
  config: Pick<MinimalProviderConfig, "endpoint" | "apiKey">
): Promise<ModelInfo[]> {
  const url = `${normalizeEndpoint(config.endpoint)}/models`;

  let response: HttpResponse;
  try {
    response = await httpRequest(url, {
      headers: { Authorization: `Bearer ${config.apiKey}` },
    });
  } catch (err) {
    throw createProviderApiError(`Network error fetching model list: ${(err as Error).message}`);
  }

  if (!response.ok) {
    throw createProviderApiError(
      `Could not fetch model list (${response.status}): ${await safeErrorDetail(response)}`
    );
  }

  const data = (await response.json()) as { data?: unknown };
  const list = data?.data;
  if (!Array.isArray(list)) {
    throw createProviderApiError("Model list response was in an unexpected shape.");
  }
  // The plain OpenAI-compatible /models shape (id/object/owned_by) doesn't carry a context-size
  // field — confirmed for OpenAI and DeepSeek directly against their docs, so those fall back to
  // name-pattern matching (core/modelContextSizes.ts) same as before. Kimi's docs document a
  // `context_length` field beyond the base shape though (platform.kimi.ai/docs/api/list-models),
  // so it's read here too — harmless no-op for providers that don't send it, since the field
  // just comes back undefined and callers already treat contextTokens as optional.
  const models = list
    .map((m: { id?: unknown; context_length?: unknown }): ModelInfo | null =>
      typeof m?.id === "string"
        ? {
            id: m.id,
            contextTokens: typeof m.context_length === "number" ? m.context_length : undefined,
          }
        : null
    )
    .filter((info: ModelInfo | null): info is ModelInfo => info !== null)
    .sort((a, b) => a.id.localeCompare(b.id));

  // Only ever a real number for Kimi in practice (see above), but harmless to call
  // unconditionally — recordLiveContextTokens no-ops on undefined.
  for (const model of models) {
    recordLiveContextTokens(config.endpoint, model.id, model.contextTokens);
  }

  return models;
}
